/**
 * Date formatting utilities for consistent display across the app
 * All functions use LOCAL time (not UTC) to avoid timezone shift issues
 */

/**
 * Parse date string to local Date object to avoid timezone issues
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {Date} Local Date object
 */
export const parseLocalDate = (dateStr) => {
  if (!dateStr) return new Date();
  const parts = dateStr.split('T')[0].split('-');
  if (parts.length >= 3) {
    return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
  }
  return new Date(dateStr);
};

/**
 * Get today's date in YYYY-MM-DD format (local time, not UTC)
 */
export const getTodayString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get current month in YYYY-MM format (local time)
 */
export const getCurrentMonthString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

/**
 * Get current datetime in YYYY-MM-DDTHH:MM format (local time)
 */
export const getCurrentDateTimeString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Convert Date object to YYYY-MM-DD string (local time)
 */
export const dateToString = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Convert Date object to YYYY-MM string (local time)
 */
export const dateToMonthString = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

/**
 * Format date string for compact mobile display
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {string} Formatted date like "15 Jan" or "15 Jan '24"
 */
export const formatDateCompact = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = parseLocalDate(dateStr);
    const now = new Date();
    const sameYear = date.getFullYear() === now.getFullYear();
    
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      ...(sameYear ? {} : { year: '2-digit' })
    });
  } catch {
    return dateStr;
  }
};

/**
 * Format date for standard display
 * @param {string} dateStr - Date string
 * @returns {string} Formatted date like "15 Jan 2024"
 */
export const formatDateStandard = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return parseLocalDate(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

/**
 * Check if date is in the future
 */
export const isFutureDate = (dateStr) => {
  if (!dateStr) return false;
  return dateStr > getTodayString();
};


/**
 * Get current datetime in full ISO format (local time, not UTC)
 * Use this instead of new Date().toISOString() to avoid UTC conversion
 */
export const getLocalISOString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const ms = String(now.getMilliseconds()).padStart(3, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}`;
};
